<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { apiErrorCode, apiErrorMessage } from '../../../../api/coLiving/errors';
import { FIELD_CLASS } from '../constants';
import { validateRoomName } from '../residentsForm';

// The rooms of one rental unit: add, rename, delete. `unit` is read from the
// parent's list, which refetches on every `changed`, so the rows stay current.
export default {
  name: 'RoomsModal',
  components: { BaseModal },
  props: {
    unit: { type: Object, required: true },
  },
  emits: ['close', 'changed'],
  data() {
    return {
      fieldClass: FIELD_CLASS,
      newName: '',
      editingId: '',
      editName: '',
      error: '',
      isBusy: false,
    };
  },
  methods: {
    ...mapActions('coLivingResidents', ['addRoom', 'editRoom', 'removeRoom']),
    startEdit(room) {
      this.editingId = room.id;
      this.editName = room.name;
      this.error = '';
    },
    cancelEdit() {
      this.editingId = '';
      this.error = '';
    },
    // Runs one change; a refusal is shown inline, success refreshes the list.
    async run(task, doneKey) {
      this.isBusy = true;
      this.error = '';
      try {
        await task();
        useAlert(this.$t(`CO_LIVING.RESIDENTS.${doneKey}`));
        this.$emit('changed');
        return true;
      } catch (err) {
        const code = apiErrorCode(err);
        const known = {
          name_taken: 'ROOM_NAME_TAKEN',
          in_use: 'ROOM_IN_USE',
          rooms_not_allowed: 'ROOMS_NOT_ALLOWED',
        };
        this.error = known[code]
          ? this.$t(`CO_LIVING.RESIDENTS.ERRORS.${known[code]}`)
          : apiErrorMessage(this, err, 'CO_LIVING.RESIDENTS.ROOM_FAILED');
        useAlert(this.error);
        return false;
      } finally {
        this.isBusy = false;
      }
    },
    async onAdd() {
      const trimmed = this.newName.trim();
      if (trimmed && validateRoomName(trimmed)) {
        this.error = this.$t('CO_LIVING.RESIDENTS.ERRORS.ROOM_NAME');
        return;
      }
      // An empty name lets the server number the room (Room N).
      const fields = trimmed ? { name: trimmed } : {};
      if (
        await this.run(
          () => this.addRoom({ unitId: this.unit.id, fields }),
          'ROOM_ADDED'
        )
      ) {
        this.newName = '';
      }
    },
    async onRename() {
      if (validateRoomName(this.editName)) {
        this.error = this.$t('CO_LIVING.RESIDENTS.ERRORS.ROOM_NAME');
        return;
      }
      const roomId = this.editingId;
      const fields = { name: this.editName.trim() };
      if (
        await this.run(
          () => this.editRoom({ unitId: this.unit.id, roomId, fields }),
          'ROOM_RENAMED'
        )
      ) {
        this.editingId = '';
      }
    },
    onDelete(room) {
      return this.run(
        () => this.removeRoom({ unitId: this.unit.id, roomId: room.id }),
        'ROOM_DELETED'
      );
    },
  },
};
</script>

<template>
  <BaseModal @close="$emit('close')">
    <template #title>
      {{ $t('CO_LIVING.RESIDENTS.ROOMS_TITLE', { unit: unit.name }) }}
    </template>

    <div data-testid="co-living-rooms">
      <p
        v-if="!unit.rooms.length"
        class="mb-3 text-sm text-slate-500 dark:text-slate-400"
      >
        {{ $t('CO_LIVING.RESIDENTS.NO_ROOMS') }}
      </p>
      <ul v-else class="mb-4 divide-y divide-slate-200 dark:divide-slate-700">
        <li
          v-for="room in unit.rooms"
          :key="room.id"
          class="flex items-center gap-2 py-2"
          :data-testid="`co-living-room-row-${room.id}`"
        >
          <template v-if="editingId === room.id">
            <input
              v-model="editName"
              type="text"
              maxlength="60"
              :class="fieldClass"
              :data-testid="`co-living-room-rename-input-${room.id}`"
              @keyup.enter="onRename"
            />
            <woot-button
              size="small"
              :disabled="isBusy"
              :data-testid="`co-living-room-rename-save-${room.id}`"
              @click="onRename"
            >
              {{ $t('CO_LIVING.COMMON.SAVE') }}
            </woot-button>
            <woot-button variant="clear" size="small" @click="cancelEdit">
              {{ $t('CO_LIVING.COMMON.CANCEL') }}
            </woot-button>
          </template>
          <template v-else>
            <span class="flex-1 text-sm text-slate-900 dark:text-slate-100">
              {{ room.name }}
              <span class="text-xs text-slate-500">
                {{
                  $t('CO_LIVING.RESIDENTS.ROOM_TENANTS', {
                    count: room.tenant_count,
                  })
                }}
              </span>
            </span>
            <woot-button
              v-tooltip.top="$t('CO_LIVING.COMMON.EDIT')"
              variant="clear"
              size="small"
              icon="edit"
              :data-testid="`co-living-room-rename-${room.id}`"
              :aria-label="$t('CO_LIVING.COMMON.EDIT')"
              @click="startEdit(room)"
            />
            <woot-button
              v-tooltip.top="$t('CO_LIVING.COMMON.DELETE')"
              variant="clear"
              size="small"
              color-scheme="alert"
              icon="delete"
              :disabled="isBusy"
              :data-testid="`co-living-room-delete-${room.id}`"
              :aria-label="$t('CO_LIVING.COMMON.DELETE')"
              @click="onDelete(room)"
            />
          </template>
        </li>
      </ul>

      <form class="flex items-center gap-2" @submit.prevent="onAdd">
        <input
          v-model="newName"
          type="text"
          maxlength="60"
          :placeholder="$t('CO_LIVING.RESIDENTS.NEW_ROOM_PLACEHOLDER')"
          :class="fieldClass"
          data-testid="co-living-room-new-name"
        />
        <woot-button
          type="submit"
          :disabled="isBusy"
          data-testid="co-living-room-add"
        >
          {{ $t('CO_LIVING.RESIDENTS.ADD_ROOM') }}
        </woot-button>
      </form>
      <p
        v-if="error"
        class="mt-2 text-xs text-red-500"
        data-testid="co-living-rooms-error"
      >
        {{ error }}
      </p>
    </div>
  </BaseModal>
</template>
